import { createSelector } from 'reselect';
import { getEntities } from '@/components/DeprecatedPortfolios/redux/selectors';
import { format } from 'date-fns';
import { getUserPortfolioKeys } from '@/components/DeprecatedPortfolios/redux/selectors';
import { toPercentage, toCurrency } from '@/utilities/formatting';
import { slugify } from '@utilities/slugify';
import { ROUTE_DEPRECATED_PORTFOLIO } from '@/constants/routes';
import { getPath } from '@/router/utils';

const dateFormat = 'MMM yy';

const formatterPercentage = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

const formatterCurrency = (value: number | null, currencyISO?: string) =>
  value !== null ? toCurrency(value, 2, currencyISO) : 'n/a';

const formatterHoldings = (isWatchlist: boolean, value?: number) => {
  if (typeof value === 'undefined') return 'n/a';
  if (isWatchlist)
    return `${value} Compan${value > 1 || value === 0 ? 'ies' : 'y'}`;
  return `${value} Holding${value > 1 || value === 0 ? 's' : ''}`;
};

export interface Entity {
  name: string;
  canonicalUrl: string;
  dateGenerated: string;
  holdingsCount: string;
  isWatchlist: boolean;
  portfolioWithNoTransactions: boolean;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  totalValueFormatted: string;
  totalGainFormatted: string;
  lastAnalysisUpdate: number | null;
  totalDividendReturn: number | null;
  totalDividendReturnFormatted: string;
}

export const getPortfolioEntitiesSelector = createSelector(
  [getEntities, getUserPortfolioKeys],
  (entities, keys): Record<string, Entity> => {
    return keys.reduce<Record<string, Entity>>((a, b) => {
      const {
        name,
        currencyISO,
        holdingsCount,
        dateGenerated,
        analysis,
        isWatchlist,
        portfolioWithNoTransactions,
      } = entities[b] || {};
      const {
        lastUpdated,
        return7D,
        return1YrAbs,
        totalValue,
        totalDividendReturn,
        totalGain,
      } = analysis || {};

      const slug = slugify(name);
      if (slug.length === 0) return a;

      const safeTotalDividendReturn =
        totalDividendReturn !== null
          ? totalDividendReturn / 100 // God damn portfolio API
          : totalDividendReturn;
      a[b] = {
        name: name,
        lastAnalysisUpdate: lastUpdated || null,
        dateGenerated:
          typeof dateGenerated !== 'undefined' && dateGenerated !== null
            ? format(dateGenerated, dateFormat)
            : 'n/a',
        holdingsCount: formatterHoldings(isWatchlist, holdingsCount),
        canonicalUrl: getPath({
          type: ROUTE_DEPRECATED_PORTFOLIO,
          payload: {
            portfolioId: b,
            name: slug,
          },
        }),
        isWatchlist,
        portfolioWithNoTransactions,
        return7D: return7D,
        return7DFormatted: formatterPercentage(return7D),
        return1YrAbs: return1YrAbs,
        return1YrAbsFormatted: formatterPercentage(return1YrAbs),
        totalValueFormatted: formatterCurrency(totalValue, currencyISO),
        totalGainFormatted: formatterCurrency(totalGain, currencyISO),
        totalDividendReturn: safeTotalDividendReturn,
        totalDividendReturnFormatted: formatterPercentage(
          safeTotalDividendReturn
        ),
      };
      return a;
    }, {});
  }
);

export const getPortfolioKeysSelector = createSelector(
  [getUserPortfolioKeys],
  (keys) => keys
);
